import { stringify } from "querystring";

import { datadogLogs } from "@datadog/browser-logs";
import { ReadonlyURLSearchParams } from "next/navigation";

import { isProduction } from "@/common/constants/env";

declare global {
  interface Window {
    GetTelemetryID: (args: { publicToken: string; submitURL: string }) => string;
  }
}

export const getTelemetryID = async (): Promise<string | null> => {
  const STYTCH_PUBLIC_TOKEN = process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN;

  if (!STYTCH_PUBLIC_TOKEN) {
    datadogLogs.logger.error("[useSignIn] STYTCH_PUBLIC_TOKEN is not defined");
    return null;
  }

  if (typeof window === "undefined" || typeof window.GetTelemetryID !== "function") {
    datadogLogs.logger.error("[useSignIn] GetTelemetryID is not defined", {
      getTelemetryID: window.GetTelemetryID,
    });
    return null;
  }

  try {
    const telemetryId: string = await Promise.race([
      window.GetTelemetryID({
        publicToken: STYTCH_PUBLIC_TOKEN,
        // It's required to use the auth.zeffy.com domain to make the Device Fingerprintering work.
        submitURL: `https://auth.zeffy.com/submit`,
      }),
      new Promise<string>((_, reject) => setTimeout(() => reject(new Error("GetTelemetryID timed out")), 3000)),
    ]);

    return telemetryId;
  } catch (error) {
    datadogLogs.logger.error("[useSignIn] GetTelemetryID failed", {
      error: JSON.stringify(error),
    });
    return null;
  }
};

export const getGoogleStytchUrl = (
  discoveryRedirectUrl?: string | null | undefined,
  loginHint?: string | null | undefined
): string => {
  const stytchBaseUrl = isProduction
    ? "https://api.stytch.com/v1/b2b/public/oauth/google/discovery/start"
    : "https://test.stytch.com/v1/b2b/public/oauth/google/discovery/start";
  const publicToken = process.env.NEXT_PUBLIC_STYTCH_PUBLIC_TOKEN;

  const params = {
    public_token: publicToken,
    discovery_redirect_url: discoveryRedirectUrl || undefined,
    provider_prompt: "select_account",
    provider_login_hint: loginHint || undefined,
  };

  return `${stytchBaseUrl}?${stringify(params)}`;
};

export const getGoogleStytchUrlWithRedirection = ({
  searchParams,
  joinEmail,
}: {
  searchParams: ReadonlyURLSearchParams;
  joinEmail?: string;
}): string => {
  const query = Object.fromEntries(searchParams?.entries() || []);

  const callbackParams = new URLSearchParams();
  if (query.redirectionUrl) {
    callbackParams.set("redirectionUrl", query.redirectionUrl);
  }
  if (query.canny === "true") {
    callbackParams.set("canny", "true");
  }
  if (query.companyID) {
    callbackParams.set("companyID", query.companyID);
  }
  if (query.redirect) {
    callbackParams.set("redirect", query.redirect);
  }
  const callbackQueryString = callbackParams.toString();
  const queryPart = callbackQueryString ? `?${callbackQueryString}` : "";
  const discoveryRedirectUrl =
    typeof window !== "undefined" ? `${window.location.origin}/auth/callback${queryPart}` : null;

  return getGoogleStytchUrl(discoveryRedirectUrl, joinEmail);
};

const GOOGLE_SSO_ORIGIN_PATH_KEY = "zeffy_google_sso_origin_path";
const CONSUMED_GOOGLE_DISCOVERY_TOKEN_KEY = "zeffy_consumed_google_discovery_token";
const GOOGLE_SSO_FALLBACK_ORIGIN_PATH = "/login";

const isSameOriginRelativePath = (path: string): boolean => path.startsWith("/") && !path.startsWith("//");

export const saveGoogleSsoOriginPath = (): void => {
  try {
    sessionStorage.setItem(GOOGLE_SSO_ORIGIN_PATH_KEY, `${window.location.pathname}${window.location.search}`);
  } catch {
    // Storage unavailable: a replayed callback falls back to the login page
  }
};

export const getGoogleSsoOriginPath = (): string => {
  try {
    const originPath = sessionStorage.getItem(GOOGLE_SSO_ORIGIN_PATH_KEY);
    return originPath && isSameOriginRelativePath(originPath) ? originPath : GOOGLE_SSO_FALLBACK_ORIGIN_PATH;
  } catch {
    return GOOGLE_SSO_FALLBACK_ORIGIN_PATH;
  }
};

export const markGoogleDiscoveryTokenConsumed = (token: string): void => {
  try {
    sessionStorage.setItem(CONSUMED_GOOGLE_DISCOVERY_TOKEN_KEY, token);
  } catch {
    // Storage unavailable: a replayed callback hits the server and shows the login error as before
  }
};

export const isGoogleDiscoveryTokenConsumed = (token: string): boolean => {
  try {
    return sessionStorage.getItem(CONSUMED_GOOGLE_DISCOVERY_TOKEN_KEY) === token;
  } catch {
    return false;
  }
};

const JOIN_GOOGLE_CONTEXT_KEY = "zeffy_join_google_context";
const JOIN_GOOGLE_CONTEXT_TTL_MS = 10 * 60 * 1000;

export interface JoinGoogleContext {
  joinEmail: string;
  joinUrl: string;
}

export const saveJoinGoogleContext = (context: JoinGoogleContext): void => {
  try {
    sessionStorage.setItem(JOIN_GOOGLE_CONTEXT_KEY, JSON.stringify({ ...context, savedAtMs: Date.now() }));
  } catch {
    // Storage unavailable (private browsing): the flow still works, minus the server-side email check
  }
};

export const clearJoinGoogleContext = (): void => {
  try {
    sessionStorage.removeItem(JOIN_GOOGLE_CONTEXT_KEY);
  } catch {
    // Storage unavailable: nothing to clear
  }
};

export const consumeJoinGoogleContext = (): JoinGoogleContext | null => {
  try {
    const raw = sessionStorage.getItem(JOIN_GOOGLE_CONTEXT_KEY);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(JOIN_GOOGLE_CONTEXT_KEY);
    const parsed = JSON.parse(raw) as JoinGoogleContext & { savedAtMs?: number };
    if (!parsed.savedAtMs || Date.now() - parsed.savedAtMs > JOIN_GOOGLE_CONTEXT_TTL_MS) {
      return null;
    }
    return { joinEmail: parsed.joinEmail, joinUrl: parsed.joinUrl };
  } catch {
    return null;
  }
};

const ADMIN_GOOGLE_CONTEXT_KEY = "zeffy_admin_google_context";
const ADMIN_GOOGLE_CONTEXT_TTL_MS = 10 * 60 * 1000;

export interface AdminGoogleContext {
  emailTarget?: string;
  organizationId?: string;
  redirectionUrl?: string | null;
}

export const saveAdminGoogleContext = (context: AdminGoogleContext): void => {
  try {
    sessionStorage.setItem(ADMIN_GOOGLE_CONTEXT_KEY, JSON.stringify({ ...context, savedAtMs: Date.now() }));
  } catch {
    // Storage unavailable (private browsing): the callback falls back to a regular Google sign-in
  }
};

export const clearAdminGoogleContext = (): void => {
  try {
    sessionStorage.removeItem(ADMIN_GOOGLE_CONTEXT_KEY);
  } catch {
    // Storage unavailable: nothing to clear
  }
};

export const consumeAdminGoogleContext = (): AdminGoogleContext | null => {
  try {
    const raw = sessionStorage.getItem(ADMIN_GOOGLE_CONTEXT_KEY);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(ADMIN_GOOGLE_CONTEXT_KEY);
    const parsed = JSON.parse(raw) as AdminGoogleContext & { savedAtMs?: number };
    if (!parsed.savedAtMs || Date.now() - parsed.savedAtMs > ADMIN_GOOGLE_CONTEXT_TTL_MS) {
      return null;
    }
    return {
      emailTarget: parsed.emailTarget,
      organizationId: parsed.organizationId,
      redirectionUrl: parsed.redirectionUrl,
    };
  } catch {
    return null;
  }
};
